import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Link as MuiLink,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Paper,
  Grid,
  Stack,
  Divider,
  useTheme
} from '@mui/material';
import { 
  Close, 
  Refresh as RefreshIcon,
  Fullscreen,
  FullscreenExit,
  MedicalServices as RadiologyIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import SimpleDicomViewer from '../radiology/SimpleDicomViewer';
import { getStorage, ref as storageRef, getBlob, getDownloadURL } from 'firebase/storage';

import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { getAuthToken } from '../../utils/authUtils';

function isRadiologyFile(url) {
  if (!url) return false;
  // Remove query parameters before checking extension
  const urlWithoutParams = url.split('?')[0];
  return urlWithoutParams.toLowerCase().endsWith('.dcm') || 
         urlWithoutParams.toLowerCase().endsWith('.dicom');
}

// Helper function to format date values
const formatDate = (dateValue) => {
  if (!dateValue) return null;
  const date = new Date(dateValue._seconds ? dateValue._seconds * 1000 : dateValue);
  return isNaN(date.getTime()) ? null : date.toLocaleString();
};

// Helper function to get status color
const getStatusColor = (status) => {
  const statusLower = status?.toLowerCase();
  switch (statusLower) {
    case 'final':
    case 'completed':
      return 'success';
    case 'preliminary':
    case 'pending':
      return 'warning';
    case 'cancelled':
    case 'error':
      return 'error';
    default:
      return 'default';
  }
};

export default function ReportDetailsModal({ open, onClose, report }) {
  const theme = useTheme();
  const [selectedTab, setSelectedTab] = useState(0);
  const [radiologyBlobs, setRadiologyBlobs] = useState({});
  const [radiologyLoading, setRadiologyLoading] = useState({});
  const [radiologyErrors, setRadiologyErrors] = useState({});
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Use refs to track loading state without causing re-renders in useCallback
  const loadingRef = useRef({});
  const loadedRef = useRef({});
  // Track which report we've already processed to prevent re-triggering
  const processedReportRef = useRef(null);

  // Get Radiology images from the report
  // Check attachments first (has signed URLs), then fall back to images array
  const allImages = useMemo(() => {
    // If attachments exist with URLs, use those (signed URLs from backend)
    if (report?.attachments && report.attachments.length > 0) {
      return report.attachments.map(att => att.url || att.path).filter(Boolean);
    }
    // Otherwise use raw images array (will need signing)
    return report?.images || [];
  }, [report?.attachments, report?.images]);

  const radiologyImages = useMemo(() => 
    allImages.filter(isRadiologyFile), 
    [allImages]
  );
  const nonRadiologyImages = useMemo(() => 
    allImages.filter(url => !isRadiologyFile(url)), 
    [allImages]
  );

  const loadRadiologyFiles = useCallback(async (imagesToLoad, reportId, forceReload = false) => {
    // Validate inputs
    if (!imagesToLoad || imagesToLoad.length === 0) {
      return;
    }
    
    const auth = getAuthToken();
    
    // Check if user is authenticated first
    if (!auth) {
      console.error('User not authenticated for radiology file access');
      setRadiologyErrors(prev => {
        const newErrors = {};
        imagesToLoad.forEach(url => {
          newErrors[url] = 'Please log in to view radiology files.';
        });
        return { ...prev, ...newErrors };
      });
      return;
    }
    
    // Check if we have a report ID to get signed URLs
    if (!reportId) {
      console.error('No report ID available for radiology file access');
      setRadiologyErrors(prev => {
        const newErrors = {};
        imagesToLoad.forEach(url => {
          newErrors[url] = 'Report ID not available for file access.';
        });
        return { ...prev, ...newErrors };
      });
      return;
    }
    
    for (let i = 0; i < imagesToLoad.length; i++) {
      const url = imagesToLoad[i];
      
      // Use refs to check if already loading or loaded (prevents infinite loop)
      if (!forceReload && (loadingRef.current[url] || loadedRef.current[url])) {
        continue;
      }
      
      // Mark as loading in ref immediately to prevent duplicate requests
      loadingRef.current[url] = true;
      setRadiologyLoading(prev => ({ ...prev, [url]: true }));
      setRadiologyErrors(prev => ({ ...prev, [url]: null }));
      
      try {
        // Skip mock bucket paths - these are placeholder URLs
        if (url.includes('mock-bucket')) {
          console.warn('Skipping mock bucket path:', url);
          setRadiologyErrors(prev => ({ 
            ...prev, 
            [url]: 'This is a placeholder image path. Please upload actual DICOM files to view them.'
          }));
          loadingRef.current[url] = false;
          continue;
        }
        
        // Try to get the file from the backend API first
        try {
          const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
          const idToken = await getAuthToken();
          
          // Get signed URL from backend
          const response = await fetch(`${API_BASE}/clinical/radiology-reports/${reportId}/image-urls`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ imagePaths: [url] })
          });
          
          if (response.ok) {
            const data = await response.json();
            const signedUrl = data.signedUrls[url];
            
            if (signedUrl) {
              // Download the file using the signed URL
              const fileResponse = await fetch(signedUrl);
              if (fileResponse.ok) {
                const blob = await fileResponse.blob();
                setRadiologyBlobs(prev => ({ ...prev, [url]: blob }));
                loadedRef.current[url] = true;
                loadingRef.current[url] = false;
                setRadiologyLoading(prev => ({ ...prev, [url]: false }));
                continue; // Successfully loaded, move to next file
              }
            }
          }
        } catch (apiError) {
          console.warn('Backend API failed, trying direct storage access:', apiError);
        }
        
        // Fallback to direct storage access if backend API fails
        const storage = getStorage();
        
        let path = url;
        if (url.startsWith('gs://')) {
          const match = url.match(/^gs:\/\/[^/]+\/(.+)$/);
          path = match ? match[1] : url;
        } else if (url.startsWith('https://')) {
          const match = url.match(/\/o\/(.+)\?/);
          path = match ? decodeURIComponent(match[1]) : url;
        }
        
        console.log('Attempting to load radiology file directly:', { originalUrl: url, resolvedPath: path });
        const fileRef = storageRef(storage, path);
        
        // Try to get download URL first (for authenticated access)
        try {
          const downloadURL = await getDownloadURL(fileRef);
          console.log('Got download URL:', downloadURL);
          
          // Get user's ID token for authenticated request
          const idToken = await getAuthToken();
          const response = await fetch(downloadURL, {
            headers: {
              'Authorization': `Bearer ${idToken}`
            }
          });
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          const blob = await response.blob();
          setRadiologyBlobs(prev => ({ ...prev, [url]: blob }));
          loadedRef.current[url] = true;
        } catch (downloadError) {
          console.warn('Failed to get download URL with auth, trying direct blob access:', downloadError);
          // Fallback to direct blob access
          const blob = await getBlob(fileRef);
          setRadiologyBlobs(prev => ({ ...prev, [url]: blob }));
          loadedRef.current[url] = true;
        }
      } catch (err) {
        console.error('Error loading radiology file:', err);
        let errorMessage = 'Failed to load Radiology file. ';
        
        if (err.message && err.message.includes('403') || err.message.includes('unauthorized')) {
          errorMessage += 'Access denied. The storage rules have been updated to allow DICOM file access. Please refresh the page and try again. If the issue persists, contact your administrator.';
        } else if (err.message && err.message.includes('404')) {
          errorMessage += 'File not found. The radiology file may have been moved or deleted.';
        } else if (err.message && err.message.includes('User not authenticated')) {
          errorMessage += 'Please log in to view radiology files.';
        } else if (err.message && err.message.includes('storage/unauthorized')) {
          errorMessage += 'Permission issue detected. Storage rules have been updated. Please refresh the page and try again.';
        } else {
          errorMessage += `${err.message || err}. Please ensure you have proper permissions.`;
        }
        
        setRadiologyErrors(prev => ({ 
          ...prev, 
          [url]: errorMessage
        }));
      } finally {
        loadingRef.current[url] = false;
        setRadiologyLoading(prev => ({ ...prev, [url]: false }));
      }
    }
  }, []); // No dependencies - function takes all inputs as parameters

  // Load Radiology files when modal opens
  useEffect(() => {
    // Only trigger loading if:
    // 1. Modal is open
    // 2. We have radiology images
    // 3. We haven't already processed this report
    if (open && radiologyImages.length > 0 && report?.id) {
      // Check if we've already started processing this report
      if (processedReportRef.current === report.id) {
        return;
      }
      processedReportRef.current = report.id;
      loadRadiologyFiles(radiologyImages, report.id, false);
    }
  }, [open, report?.id, radiologyImages, loadRadiologyFiles]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    if (newValue === 0 && radiologyImages.length > 0) {
      setCurrentImageIndex(0);
    }
  };

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleClose = () => {
    setIsFullscreen(false);
    // Reset refs when closing to allow fresh load next time
    loadingRef.current = {};
    loadedRef.current = {};
    processedReportRef.current = null;
    onClose();
  };
  
  // Reset state when report changes
  useEffect(() => {
    if (report?.id) {
      loadingRef.current = {};
      loadedRef.current = {};
      setRadiologyBlobs({});
      setRadiologyErrors({});
      setRadiologyLoading({});
    }
  }, [report?.id]);

  if (!report) return null;

  const currentRadiologyUrl = radiologyImages[currentImageIndex];
  const currentRadiologyBlob = currentRadiologyUrl ? radiologyBlobs[currentRadiologyUrl] : null;
  const currentRadiologyLoading = currentRadiologyUrl ? radiologyLoading[currentRadiologyUrl] : false;
  const currentRadiologyError = currentRadiologyUrl ? radiologyErrors[currentRadiologyUrl] : null;

  // Slider settings using theme colors
  const sliderSettings = {
    dots: nonRadiologyImages.length > 1,
    arrows: true,
    infinite: false,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          arrows: false,
          dots: true
        }
      }
    ]
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      fullScreen={isFullscreen}
      aria-labelledby="report-details-dialog-title"
      PaperProps={{
        sx: {
          height: isFullscreen ? '100vh' : 'auto',
          maxHeight: isFullscreen ? '100vh' : '90vh',
          borderRadius: isFullscreen ? 0 : 2,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle 
        id="report-details-dialog-title"
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          py: 1.5,
          px: 2,
          bgcolor: 'primary.main',
          color: 'white'
        }}
      >
        <Box>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: 'white' }}>
            {report.testType || report.reportType || 'Report Details'}
          </Typography>
          {report.procedure_order_id && (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.25 }}>
              Order ID: {report.procedure_order_id}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={0.5}>
          {(radiologyImages.length > 0 || nonRadiologyImages.length > 0) && (
            <IconButton 
              onClick={handleFullscreen}
              size="small"
              aria-label={isFullscreen ? 'exit fullscreen' : 'fullscreen'}
              sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
            >
              {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          )}
          <IconButton 
            onClick={handleClose}
            size="small"
            aria-label="close"
            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
          >
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : '#f8f9fa' }}>
        {/* Report Information Section */}
        <Paper elevation={0} sx={{ p: 2.5, m: 2, mb: 0, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Report Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">Provider</Typography>
              <Typography variant="body2">{report.provider_id || 'N/A'}</Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip 
                  label={report.review_status || report.reportStatus || 'Unknown'} 
                  size="small"
                  color={getStatusColor(report.review_status || report.reportStatus)}
                />
              </Box>
            </Grid>
            
            {formatDate(report.date_collected) && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">Collection Date</Typography>
                <Typography variant="body2">{formatDate(report.date_collected)}</Typography>
              </Grid>
            )}
            
            {formatDate(report.date_report) && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">Report Date</Typography>
                <Typography variant="body2">{formatDate(report.date_report)}</Typography>
              </Grid>
            )}
            
            {report.comments && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Comments</Typography>
                <Typography variant="body2">{report.comments}</Typography>
              </Grid>
            )}
            
            {report.reportText && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Findings</Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    mt: 0.5, 
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  <Typography variant="body2">{report.reportText}</Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Images Section */}
        {(radiologyImages.length > 0 || nonRadiologyImages.length > 0) && (
          <Paper elevation={0} sx={{ m: 2, mt: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Tabs 
              value={selectedTab} 
              onChange={handleTabChange} 
              sx={{ 
                bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : '#f5f5f5',
                borderBottom: 1, 
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minHeight: 48,
                  '&.Mui-selected': {
                    color: 'primary.main',
                    fontWeight: 600
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'primary.main',
                  height: 3
                }
              }}
            >
              {radiologyImages.length > 0 && (
                <Tab 
                  label={`DICOM Images (${radiologyImages.length})`}
                  icon={<RadiologyIcon sx={{ fontSize: 18 }} />}
                  iconPosition="start"
                />
              )}
              {nonRadiologyImages.length > 0 && (
                <Tab 
                  label={`Other Images (${nonRadiologyImages.length})`}
                  icon={<ImageIcon sx={{ fontSize: 18 }} />}
                  iconPosition="start"
                />
              )}
            </Tabs>

            {/* DICOM Images Tab */}
            {selectedTab === 0 && radiologyImages.length > 0 && (
              <Box sx={{ p: 2, bgcolor: 'white' }}>
                <Box 
                  sx={{ 
                    p: 2, 
                    minHeight: isFullscreen ? 'calc(100vh - 350px)' : 500,
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    overflow: 'hidden',
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : '#f5f5f5',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  {currentRadiologyLoading ? (
                    <Stack alignItems="center" spacing={2}>
                      <CircularProgress color="primary" />
                      <Typography color="text.secondary">Loading DICOM file...</Typography>
                    </Stack>
                  ) : currentRadiologyError ? (
                    <Alert 
                      severity="error" 
                      sx={{ width: '100%', maxWidth: 600 }}
                      action={
                        <Button 
                          color="inherit" 
                          size="small" 
                          onClick={() => {
                            // Reset refs for this URL to allow retry
                            loadingRef.current[currentRadiologyUrl] = false;
                            loadedRef.current[currentRadiologyUrl] = false;
                            setRadiologyErrors(prev => ({ ...prev, [currentRadiologyUrl]: null }));
                            setRadiologyBlobs(prev => ({ ...prev, [currentRadiologyUrl]: null }));
                            loadRadiologyFiles([currentRadiologyUrl], report?.id, true);
                          }}
                        >
                          Retry
                        </Button>
                      }
                    >
                      {currentRadiologyError}
                    </Alert>
                  ) : currentRadiologyBlob ? (
                    <Box sx={{ 
                      width: '100%', 
                      height: isFullscreen ? 'calc(100vh - 400px)' : 450,
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <SimpleDicomViewer
                        dicomFile={currentRadiologyBlob}
                        patientInfo={{ name: report.patientName, id: report.patientId }}
                        studyInfo={{ description: report.testType || report.reportType }}
                        onError={(error) => {
                          setRadiologyErrors(prev => ({ ...prev, [currentRadiologyUrl]: error }));
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography color="text.secondary">No DICOM file loaded</Typography>
                  )}
                </Box>

                {/* DICOM file selector */}
                {radiologyImages.length > 1 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                      Select Image:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {radiologyImages.map((url, idx) => {
                        const fileName = url.split('/').pop();
                        const shortName = fileName.length > 20 ? fileName.substring(0, 17) + '...' : fileName;
                        return (
                          <Chip
                            key={idx}
                            label={`${shortName} (${idx + 1})`}
                            variant={idx === currentImageIndex ? 'filled' : 'outlined'}
                            color={idx === currentImageIndex ? 'primary' : 'default'}
                            onClick={() => setCurrentImageIndex(idx)}
                            clickable
                            size="small"
                            sx={{ 
                              maxWidth: 200,
                              mb: 1,
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }
                            }}
                            title={fileName}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}

            {/* Other Images Tab */}
            {((selectedTab === 0 && radiologyImages.length === 0) || (selectedTab === 1 && nonRadiologyImages.length > 0)) && (
              <Box sx={{ p: 2, bgcolor: 'white' }}>
                <Box 
                  sx={{ 
                    maxWidth: 700, 
                    mx: 'auto', 
                    p: 3,
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : '#fafafa',
                    borderRadius: 2,
                    '& .slick-dots': {
                      bottom: -30
                    },
                    '& .slick-dots li button:before': {
                      fontSize: 12,
                      color: theme.palette.primary.main
                    },
                    '& .slick-dots li.slick-active button:before': {
                      color: theme.palette.primary.main
                    },
                    '& .slick-prev, & .slick-next': {
                      width: 30,
                      height: 30,
                      zIndex: 1
                    },
                    '& .slick-prev': {
                      left: -35
                    },
                    '& .slick-next': {
                      right: -35
                    },
                    '& .slick-prev:before, & .slick-next:before': {
                      fontSize: 30,
                      color: theme.palette.primary.main
                    },
                    '& .slick-prev:hover:before, & .slick-next:hover:before': {
                      color: theme.palette.primary.dark
                    }
                  }}
                >
                  <Slider {...sliderSettings}>
                    {nonRadiologyImages.map((url, idx) => {
                      // Extract clean filename from URL (remove query params)
                      const cleanFileName = url.split('/').pop()?.split('?')[0] || `Image ${idx + 1}`;
                      return (
                        <Box 
                          key={idx} 
                          sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            gap: 1, 
                            p: 2 
                          }}
                        >
                          <Box
                            component="img"
                            src={url} 
                            alt={cleanFileName} 
                            sx={{ 
                              maxWidth: '100%', 
                              maxHeight: 350, 
                              objectFit: 'contain',
                              borderRadius: 1,
                              boxShadow: 2
                            }} 
                          />
                          <Typography variant="caption" color="text.secondary">
                            {cleanFileName}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Slider>
                </Box>
              </Box>
            )}
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'white' }}>
        {radiologyImages.length > 0 && (
          <Button 
            onClick={() => {
              // Reset refs and state for fresh reload
              loadingRef.current = {};
              loadedRef.current = {};
              processedReportRef.current = null;
              setRadiologyErrors({});
              setRadiologyBlobs({});
              loadRadiologyFiles(radiologyImages, report?.id, true);
            }}
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
          >
            Refresh Files
          </Button>
        )}
        <Button onClick={handleClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
